import { computed, ref, unref, watch } from 'vue';
import GamificationAPI from 'dashboard/api/gamification';

const responseData = response => response?.data?.data ?? response?.data ?? null;

const userIdValue = userId => {
  if (typeof userId === 'function') return userId();
  return unref(userId);
};

export function useGamification({ userId = null, autoLoad = true } = {}) {
  const settings = ref(null);
  const profile = ref(null);
  const leaderboard = ref([]);
  const activities = ref([]);
  const achievements = ref([]);
  const loading = ref(false);
  const error = ref(null);
  const unavailable = ref(false);

  const enabled = computed(() => settings.value?.enabled === true);
  const hasData = computed(
    () => enabled.value && Boolean(profile.value || leaderboard.value.length)
  );

  const resetData = () => {
    profile.value = null;
    leaderboard.value = [];
    activities.value = [];
    achievements.value = [];
  };

  const load = async () => {
    loading.value = true;
    error.value = null;
    unavailable.value = false;

    try {
      const settingsResponse = await GamificationAPI.settings();
      settings.value = responseData(settingsResponse);

      if (!enabled.value) {
        resetData();
        return settings.value;
      }

      const requests = [
        GamificationAPI.leaderboard({ period: 'month' }),
        GamificationAPI.activities({ limit: 6 }),
        GamificationAPI.achievements(),
      ];

      if (userIdValue(userId)) {
        requests.push(GamificationAPI.profile(userIdValue(userId)));
      }

      const [
        leaderboardResponse,
        activitiesResponse,
        achievementsResponse,
        profileResponse,
      ] = await Promise.all(requests);

      leaderboard.value = responseData(leaderboardResponse) || [];
      activities.value = responseData(activitiesResponse) || [];
      achievements.value = responseData(achievementsResponse) || [];
      profile.value = profileResponse ? responseData(profileResponse) : null;

      return settings.value;
    } catch (loadError) {
      error.value = loadError;
      unavailable.value = true;
      resetData();
      return null;
    } finally {
      loading.value = false;
    }
  };

  if (autoLoad) {
    watch(
      () => userIdValue(userId),
      () => load(),
      { immediate: true }
    );
  }

  return {
    settings,
    profile,
    leaderboard,
    activities,
    achievements,
    loading,
    error,
    unavailable,
    enabled,
    hasData,
    load,
  };
}
